import { Region } from "@/api/studio/data/types/region.type";
import { getUniqueLabel } from "@/features/studies/utils/get-unique-label";

interface AssetMetaData {
  width: number;
  height: number;
}

interface PasteRegionsResult {
  regionsToAdd: Region[];
  skipped: number;
}

/*
 * processRegionsForPaste function
 *
 * Processes a list of regions for pasting into a target asset.
 * Skips regions that are too large to fit, clamps positions to canvas bounds,
 * and ensures all labels are unique against existing labels.
 */
export function processRegionsForPaste(regions: Region[], targetMeta: AssetMetaData, existingLabels: string[]): PasteRegionsResult {
  // Work from a copy so we can track labels added during this batch without mutating the array
  const labels = [...existingLabels];
  const regionsToAdd: Region[] = [];
  let skipped = 0;

  for (const region of regions) {
    // Derive the bounding box of the region from its points
    const xs = region.points.map((p) => p.x);
    const ys = region.points.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    // If the region is wider or taller than the target canvas it can never fit — skip it
    if (maxX - minX > targetMeta.width || maxY - minY > targetMeta.height) {
      skipped++;
      continue;
    }

    // Clamp: if the right or bottom edge overflows the canvas, shift the region back inside
    const offsetX = maxX > targetMeta.width ? targetMeta.width - maxX : 0;
    const offsetY = maxY > targetMeta.height ? targetMeta.height - maxY : 0;
    const newPoints = region.points.map((p) => ({ x: p.x + offsetX, y: p.y + offsetY }));

    // Ensure the label is unique within the target asset; register it so subsequent regions in the batch don't collide
    const label = getUniqueLabel(region.label, labels);
    labels.push(label);

    // Assign a fresh ID so the pasted region is independent of its source
    regionsToAdd.push({ ...region, id: crypto.randomUUID(), label, points: newPoints, priority: region.priority ?? 0 });
  }

  return { regionsToAdd, skipped };
}
